/**
 * The site's icon set. Inline SVG so every icon inherits currentColor from its
 * chip and the site ships no icon dependency. Shared by the front page and the
 * building-management page.
 */

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const CheckIcon = () => (
  <svg {...svgProps} strokeWidth="2.2"><path d="m5 12 4.5 4.5L19 7" /></svg>
);

/** The counterpart to CheckIcon, for the "not in a home machine" column. */
export const CrossIcon = () => (
  <svg {...svgProps} strokeWidth="2.2"><path d="M6 6l12 12M18 6 6 18" /></svg>
);

export const BeddingIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M3 17v-3a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3" />
    <path d="M5 12V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4" />
    <path d="M3 17h18v3H3z" />
  </svg>
);

export const DropIcon = () => (
  <svg {...svgProps} strokeWidth="1.8"><path d="M12 3s6 5.5 6 10a6 6 0 0 1-12 0c0-4.5 6-10 6-10Z" /></svg>
);

export const BoltIcon = () => (
  <svg {...svgProps} strokeWidth="1.8"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" /></svg>
);

export const LeafIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 3 2 8.5-2 12a7 7 0 0 1-6 6Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6" />
  </svg>
);

export const ShieldIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M12 3 4.5 6v5.5c0 4.6 3.2 8 7.5 9.5 4.3-1.5 7.5-4.9 7.5-9.5V6L12 3z" />
    <path d="m9.5 12 1.8 1.8L15 10" />
  </svg>
);

export const LayersIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M3 7.5 12 3l9 4.5-9 4.5-9-4.5Z" />
    <path d="M3 12l9 4.5L21 12" />
    <path d="M3 16.5 12 21l9-4.5" />
  </svg>
);

export const DuvetIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M4 9V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3" />
    <path d="M2 9h20l-1.2 9.5a2 2 0 0 1-2 1.7H5.2a2 2 0 0 1-2-1.7L2 9Z" />
  </svg>
);

export const BuildingIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M4 11 12 4l8 7" />
    <path d="M6 10v9h12v-9" />
    <path d="M10 19v-5h4v5" />
  </svg>
);

export const SavingsIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <circle cx="12" cy="12" r="9" />
    <path d="M14.5 9.2c-.4-.9-1.4-1.4-2.5-1.4-1.5 0-2.6.8-2.6 2 0 2.6 5.3 1.3 5.3 4 0 1.2-1.2 2-2.7 2-1.2 0-2.2-.5-2.6-1.4" />
  </svg>
);

export const StarIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="m12 3 2.7 5.6 6.2.9-4.5 4.3 1.1 6.2L12 17l-5.5 2.9 1.1-6.2L3.1 9.5l6.2-.9L12 3z" />
  </svg>
);

export const ClockIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.2l3.4 2" />
  </svg>
);

export const TagIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M3 11.5V4a1 1 0 0 1 1-1h7.5a1 1 0 0 1 .7.3l8.5 8.5a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 12.2a1 1 0 0 1-.3-.7Z" />
    <circle cx="7.5" cy="7.5" r="1.4" />
  </svg>
);

export const TapToPayIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <rect x="4" y="2.5" width="11" height="19" rx="2.5" />
    <path d="M8.5 18.5h2" />
    <path d="M18 9.5a4 4 0 0 1 0 5" />
    <path d="M20.5 7a7.5 7.5 0 0 1 0 10" />
  </svg>
);

/** The scan-to-pay rails — PromptPay, Alipay, WeChat Pay. Three finder squares
    and a scatter of modules: a QR read at a glance rather than a literal code. */
export const QrIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <path d="M14 14h3v3h-3z" />
    <path d="M20.5 14v3" />
    <path d="M14 20.5h6.5" />
  </svg>
);

export const CardIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 9.5h19" />
    <path d="M6 14.5h3.5" />
  </svg>
);

export const PhoneIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
    <path d="M10.5 18.5h3" />
  </svg>
);

export const TrendIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M3 17.5 9.5 11l4 4L21 7.5" />
    <path d="M15.5 7.5H21V13" />
  </svg>
);

export const MegaphoneIcon = () => (
  <svg {...svgProps} strokeWidth="1.8">
    <path d="M4 10.5 18 4v16L4 13.5z" />
    <path d="M4 10.5H3a2 2 0 0 0 0 4h1z" />
    <path d="m7.5 15 1.5 5.5h3L10.5 15" />
  </svg>
);
